/* eslint-disable react-hooks/exhaustive-deps */
import { SyncOutlined } from '@ant-design/icons'
import {
  Button,
  DatePicker,
  Form,
  FormInstance,
  InputNumber,
  notification,
  Select,
  Tooltip,
} from 'antd/es'
import { useWatch } from 'antd/es/form/Form'
import TextArea from 'antd/es/input/TextArea'
import dayjs, { Dayjs } from 'dayjs'
import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { INCOTERM_GROUPS } from '@constants'
import { useUser } from '@contexts'
import {
  DeliveryNoteLabel,
  useDeliveryNotes,
  useDestinations,
  useEquipment,
  useMaterials,
  usePurchaseOrdersByMaterial,
} from '@hooks'
import { Inco1Type, RoleEnum, SapItemSupplier, Supplier, TripTypeEnum } from '@types'
import { CutoffTimeUtils, DeliveryNoteUtils, OtherUtils, StringUtils } from '@utils'

import { FlexCol, FlexRow, StyledInput, StyledRow } from '../../../../components/StyledComponents'
import { DATE_KEYS } from '../../../../constants/dateKeys'
import { mapDestinationsToCombo } from '../../../../utils/array-entity-to-combo'
import { limitIntEntry, preventEvent } from '../../../../utils/inputs'

const { classesToString } = StringUtils

const initialValuesNewTicket = {
  count: 1,
  depotId: undefined,
  materialId: undefined,
  equipmentId: undefined,
  destinationId: undefined,
  ticketsDate: dayjs(
    `${dayjs().add(1, 'd').format('DD-MM-YYYY')} ${dayjs().hour() + 1}:00:00`,
    DATE_KEYS.FORMAT.FULL_TIME,
  ).toDate(),
  comments: undefined,
  documentNumberId: undefined,
}

const handleFilterOptions = (input: string, option: any) => {
  if (option.label) return option.label.toString().toLowerCase().includes(input.toLowerCase())
  return true
}

const today = dayjs()
const tomorrow = today.clone().add(1, 'day').startOf('day')

interface TicketAux {
  count: number
  supplierId?: number | undefined
  depotId?: number | undefined
  materialId?: number | undefined
  equipmentId?: number | undefined
  destinationId?: number | undefined
  ticketDate?: Date | Date[] | undefined
  carrierId?: number | undefined
  comments?: string | undefined
  approved?: boolean
  tripType?: TripTypeEnum
}

type Dates = Date[] | undefined

interface AddTicketFormSupplierProps {
  form: FormInstance<any>
  handlePrimaryButtonDisabling: (value: boolean) => void
  editable: boolean | undefined
  setEditable: Dispatch<SetStateAction<boolean | undefined>>
  dates: Dates
  setDates: Dispatch<SetStateAction<Dates>>
  isWarning: boolean | undefined
  setIsWarning: Dispatch<SetStateAction<boolean | undefined>>
  maxAllowedDaysForSupplierRequest?: number
  cutoffTime: string
}

const AddTicketFormSupplier: FC<PropsWithChildren<AddTicketFormSupplierProps>> = ({
  handlePrimaryButtonDisabling,
  editable,
  setEditable,
  dates,
  setDates,
  isWarning,
  setIsWarning,
  form,
  maxAllowedDaysForSupplierRequest,
  cutoffTime,
}) => {
  // hooks
  const { t } = useTranslation()
  const { state: user } = useUser()
  const deliveryNoteMinLength = DeliveryNoteUtils.DEFAULT_MIN_LENGTH
  const deliveryNoteMaxLength = DeliveryNoteUtils.DEFAULT_MAX_LENGTH
  const deliveryNoteValidationMessage = t('message_delivery_note_number', {
    min: deliveryNoteMinLength,
    max: deliveryNoteMaxLength,
  })
  const documentNumberId = useWatch('documentNumberId', form)
  const materialId = useWatch('materialId', form)
  const typedDeliveryNote = useWatch('number', form)
  const isDeliveryNoteFieldEnabled = !!documentNumberId && !!materialId
  const isCommercial = user?.roles?.[0]?.name === RoleEnum.COMMERCIAL
  const commercialSuppliers = useMemo(
    () =>
      isCommercial
        ? (user?.userDetail?.commercialSupplier?.suppliers ?? []).filter(
            s => Boolean(s) && !s.disableAt,
          )
        : [],
    [isCommercial, user?.userDetail?.commercialSupplier?.suppliers],
  )
  const selectedCommercialSupplierId = useWatch('supplierId', form) as number | undefined
  const supplier = useMemo((): Supplier | undefined => {
    if (isCommercial) {
      const id = selectedCommercialSupplierId ?? commercialSuppliers[0]?.id
      return commercialSuppliers.find(s => s.id === id) ?? commercialSuppliers[0]
    }
    return user?.userDetail?.supplier as Supplier | undefined
  }, [isCommercial, commercialSuppliers, selectedCommercialSupplierId, user?.userDetail?.supplier])
  const { equipments } = useEquipment()

  // states
  const [plantId, setPlantId] = useState<string>()
  const [isCalendarVisible, setIsCalendarVisible] = useState<boolean>(false)
  const [required, setRequired] = useState<boolean>(true)
  const [isTripCfr, setIsTripCfr] = useState<boolean>(false)
  const [initialValuesAux, setInitialValuesAux] = useState<TicketAux>()
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<SapItemSupplier>()
  const [enableDocument, setEnableDocument] = useState<boolean>(false)
  const [baselineDocumentNumberId, setBaselineDocumentNumberId] = useState<string>()

  // hooks with dependencies
  const { purchaseOrders } = usePurchaseOrdersByMaterial(supplier?.sapId)
  const { materials, materialsOptionsSapCode } = useMaterials(
    selectedPurchaseOrder?.DocumentNumberID,
    'BLOQUEADA (Saldo Insuficiente) - ',
  )
  const currentDocumentNumberId = selectedPurchaseOrder?.DocumentNumberID
  const {
    deliveryNotesOptions,
    isDeliveryNotesLoading,
    isSyncingDeliveryNotes,
    showDeliveryNoteNotFoundWarning,
    syncDeliveryNotes,
  } = useDeliveryNotes(currentDocumentNumberId, materialId, materials, {
    typedDeliveryNote,
  })

  const destinationCodesFromPurchaseOrders = useMemo(
    () => [...new Set(purchaseOrders?.map(({ PlantID }) => PlantID))],
    [purchaseOrders],
  )
  const { destinations } = useDestinations(supplier?.sapId)

  const tripTypeValue = useWatch('tripType', form) as TripTypeEnum

  useEffect(() => {
    setIsTripCfr(tripTypeValue === TripTypeEnum.CFR)
  }, [tripTypeValue])

  const isRequiredMsg = t('globals.obligatory_field')

  const closeCalendar = () => {
    setIsCalendarVisible(false)
  }

  const resetFields = () => {
    setInitialValuesAux(setInitialValues())
    setTimeout(form.resetFields, 1)
  }

  const setInitialValues = useCallback(
    () => ({
      ...initialValuesNewTicket,
      tripType: !supplier?.sapId ? TripTypeEnum.TRANSFERENCE : undefined,
      ...(isCommercial && supplier?.id ? { supplierId: supplier.id } : {}),
    }),
    [supplier, isCommercial, commercialSuppliers],
  )

  const handleCommercialSupplierChange = useCallback(() => {
    form.setFieldsValue({
      depotId: undefined,
      destinationId: undefined,
      documentNumberId: undefined,
      materialId: undefined,
      number: undefined,
    })
    setPlantId(undefined)
    setSelectedPurchaseOrder(undefined)
    setBaselineDocumentNumberId(undefined)
  }, [form])

  useEffect(() => {
    if (isCommercial && commercialSuppliers.length && selectedCommercialSupplierId == null) {
      form.setFieldsValue({ supplierId: commercialSuppliers[0].id })
    }
  }, [isCommercial, commercialSuppliers, form, selectedCommercialSupplierId])

  const handleDestinationSelect = useCallback(
    (value: number) => {
      handlePrimaryButtonDisabling(true)
      form.setFieldsValue({ materialId: undefined, documentNumberId: undefined, number: undefined })
      const destinationPlantId =
        destinations && destinations.find(destination => destination.id === value)
      const filteredPlantId = destinationPlantId?.plantId
      setPlantId(filteredPlantId)
    },
    [destinations],
  )

  const isDateAfterToday = useCallback(
    () => dates && dates[0].getTime() < dayjs().toDate().getTime(),
    [dates],
  )

  const applyCutoffTime = useCallback(
    (value: string, inputDates?: Date[]) =>
      inputDates?.map(date => CutoffTimeUtils.applyCutoffTimeToDate(date, value)),
    [],
  )

  const calendarFooter = useCallback(
    () => (
      <FlexRow
        style={{
          justifyContent: 'end',
          padding: '0.5rem',
        }}
      >
        <Button type="primary" onClick={closeCalendar}>
          Aceptar
        </Button>
      </FlexRow>
    ),
    [],
  )

  const handleMaterialsOptions = useCallback(
    (value: number | undefined) => {
      form.setFieldsValue({ materialId: value, number: undefined })
      handlePrimaryButtonDisabling(false)
    },
    [materials],
  )
  const handleTimeChange = () => {
    const nextDates = applyCutoffTime(cutoffTime, dates)
    if (nextDates) {
      setDates(nextDates)
    }

    if (dates?.length && isDateAfterToday()) {
      if (!isTripCfr) {
        handlePrimaryButtonDisabling(true)
      }
    }
  }

  const handleChangeDate = useCallback(
    (thisDates?: Dayjs[]) => {
      const isCfr = tripTypeValue === TripTypeEnum.CFR || isTripCfr

      const sorted = thisDates?.map(d => d.toDate()).sort((a, b) => (a < b ? -1 : 1))
      const datesWithCutoff = applyCutoffTime(cutoffTime, sorted)
      setDates(datesWithCutoff)

      if (thisDates) {
        form.setFieldsValue({ withdrawalDates: datesWithCutoff })
      }

      if (isCfr) {
        handlePrimaryButtonDisabling(false)
      }
    },
    [tripTypeValue, isTripCfr, cutoffTime, applyCutoffTime],
  )

  const clearDates = () => {
    setDates([])
    form.setFieldValue('withdrawalDates', [])
  }

  const handlePurchaseDocumentChange = useCallback(
    (selectedDocumentNumberId?: string) => {
      const order = purchaseOrders?.find(po => po.DocumentNumberID === selectedDocumentNumberId)
      if (!order) {
        form.setFieldsValue({ documentNumberId: undefined, tripType: undefined, number: undefined })
        setSelectedPurchaseOrder(undefined)
        setDates([])
        form.resetFields(['withdrawalDates'])
        return
      }

      const newTripType = INCOTERM_GROUPS.FOB.includes(order.Inco1 as any)
        ? TripTypeEnum.FOB
        : INCOTERM_GROUPS.CFR.includes(order.Inco1 as any)
        ? TripTypeEnum.CFR
        : TripTypeEnum.TRANSFERENCE

      if (tripTypeValue && newTripType !== tripTypeValue) {
        setDates([])
        form.resetFields(['withdrawalDates'])
      }

      setSelectedPurchaseOrder(order)
      form.setFieldsValue({
        documentNumberId: selectedDocumentNumberId,
        tripType: newTripType,
        number: undefined,
      })
      handlePrimaryButtonDisabling(true)
    },
    [purchaseOrders, tripTypeValue, form, handlePrimaryButtonDisabling, setDates],
  )

  const TOOLTIP_TEXTS: Record<TripTypeEnum, string> = {
    [TripTypeEnum.FOB]: t('selected_Incoterm.fob'),
    [TripTypeEnum.CFR]: t('selected_Incoterm.cfr'),
    [TripTypeEnum.TRANSFERENCE]: '',
  }

  const getTooltipText = (tripType?: TripTypeEnum) =>
    TOOLTIP_TEXTS[tripType || TripTypeEnum.TRANSFERENCE] || ''

  const TripTypeDisplay: React.FC<{ tripType?: TripTypeEnum }> = ({ tripType }) => {
    const displayValue = tripType ? t(`trip_type.${tripType}`) : ''
    return (
      <Tooltip title={getTooltipText(tripType)}>
        <span className="ticket-field-value">{displayValue}</span>
      </Tooltip>
    )
  }

  useEffect(() => {
    if (!initialValuesAux) {
      setInitialValuesAux(setInitialValues())
    }
  }, [])

  useEffect(() => {
    setPlantId(undefined)
    setRequired(true)
    setEditable(true)
    setEnableDocument(!!supplier?.sapId)
    setBaselineDocumentNumberId(undefined)
    setInitialValuesAux(setInitialValues())
    resetFields()
  }, [supplier?.id, isCommercial])

  useEffect(() => {
    if (!baselineDocumentNumberId && selectedPurchaseOrder?.DocumentNumberID) {
      setBaselineDocumentNumberId(selectedPurchaseOrder.DocumentNumberID)
    }
  }, [baselineDocumentNumberId, selectedPurchaseOrder?.DocumentNumberID])

  const handleDeliveryNoteChange = useCallback(
    (value: string | string[] | undefined) => {
      const normalized = DeliveryNoteUtils.normalizeValue(value)
      if (
        normalized &&
        !DeliveryNoteUtils.isValidDeliveryNote(
          normalized,
          deliveryNoteMinLength,
          deliveryNoteMaxLength,
        )
      ) {
        notification.error({
          message: deliveryNoteValidationMessage,
          key: 'materialError',
        })
      }
      const materialForSelectedDeliveryNote = materials?.filter(material =>
        material.sapItemSupplier?.sapItemSupplierDeliveryNotes?.some(
          item => item.deliveryNote === normalized,
        ),
      )[0]
      materialChange(materialForSelectedDeliveryNote?.idSap)
    },
    [materials, deliveryNoteMinLength, deliveryNoteMaxLength, deliveryNoteValidationMessage],
  )

  const materialChange = useCallback(
    (value: number | undefined) => {
      const newSelectedMaterial = materials?.find(material => material.idSap === value)
      form.setFieldsValue({ material: newSelectedMaterial?.idSap })
    },
    [form, materials],
  )

  useEffect(() => {
    if (!!dates?.length) {
      setIsWarning(false)
      handleTimeChange()
    }
    form.setFieldsValue({
      withdrawalDates: dates,
    })
  }, [dates, cutoffTime])

  useEffect(() => {
    if (selectedPurchaseOrder) {
      form.setFieldsValue({ materialId: undefined, number: undefined })

      const value = INCOTERM_GROUPS.FOB.includes(selectedPurchaseOrder?.Inco1 as Inco1Type)
        ? TripTypeEnum.FOB
        : INCOTERM_GROUPS.CFR.includes(selectedPurchaseOrder?.Inco1 as Inco1Type)
        ? TripTypeEnum.CFR
        : undefined

      form.setFieldsValue({ tripType: value })
    }
  }, [selectedPurchaseOrder])

  useEffect(() => {
    if (tripTypeValue === TripTypeEnum.CFR) {
      const currentValue = form.getFieldValue('count')
      if (currentValue !== 1) {
        form.setFieldsValue({ count: 1 })
      }

      if (dates && dates.length > 1) {
        const singleDate = [dates[0]]
        setDates(singleDate)
        form.setFieldsValue({
          withdrawalDates: singleDate,
        })
      }
    }
  }, [tripTypeValue, form, dates])

  if (!isCommercial && !user?.userDetail?.supplier) return null
  if (isCommercial && commercialSuppliers.length === 0) {
    return (
      <div style={{ padding: 16 }}>
        {t('suppliers_router.new_ticket_modal.no_commercial_suppliers')}
      </div>
    )
  }
  if (!supplier) return null

  return (
    <Form form={form} initialValues={initialValuesAux} name="ticket-form" className="ticket-form">
      <FlexCol className="ticket-form__column ticket-data">
        <FlexCol className="form-section">
          {isCommercial && commercialSuppliers.length > 0 && (
            <StyledRow className="new-ticket-form-row">
              <Form.Item
                label={t('suppliers_router.new_ticket_modal.commercial_supplier')}
                name="supplierId"
                rules={[{ required: true, message: isRequiredMsg }]}
              >
                <Select
                  showSearch
                  filterOption={handleFilterOptions}
                  getPopupContainer={(triggerNode: HTMLElement) =>
                    triggerNode.parentNode as HTMLElement
                  }
                  placeholder={t('globals.placeholder_select')}
                  options={commercialSuppliers.map(s => ({
                    label: s.businessName || s.tradeName,
                    value: s.id,
                  }))}
                  onChange={handleCommercialSupplierChange}
                />
              </Form.Item>
            </StyledRow>
          )}
          {/* CANTIDAD */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.quantity')}
              name="count"
              initialValue={tripTypeValue === TripTypeEnum.CFR ? 1 : undefined}
              rules={[
                {
                  required: true,
                  message: isRequiredMsg,
                },
                ...(tripTypeValue === TripTypeEnum.CFR
                  ? [
                      {
                        validator: (_: unknown, value: number) =>
                          value === 1
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error(
                                  t('suppliers_router.new_ticket_modal.cfr_quantity_error'),
                                ),
                              ),
                      },
                    ]
                  : []),
              ]}
            >
              <>
                <InputNumber
                  onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => preventEvent(event)}
                  onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => limitIntEntry(event)}
                  min={1}
                  max={10}
                  disabled={tripTypeValue === TripTypeEnum.CFR}
                  style={{ width: '100%' }}
                  value={form.getFieldValue('count')}
                />
              </>
            </Form.Item>
          </StyledRow>
          {/* ORIGEN */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.origin')}
              rules={[
                {
                  required,
                  message: isRequiredMsg,
                },
              ]}
              name="depotId"
            >
              <Select
                showSearch
                filterOption={handleFilterOptions}
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder={t('globals.placeholder_select')}
                options={(supplier?.depots ?? []).map(depot => ({
                  label: depot.tag,
                  value: depot.id,
                }))}
              />
            </Form.Item>
          </StyledRow>
          {/* DESTINO */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.destination')}
              rules={[
                {
                  required,
                  message: isRequiredMsg,
                },
              ]}
              name="destinationId"
            >
              <Select
                showSearch
                filterOption={handleFilterOptions}
                allowClear
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder={t('globals.placeholder_select')}
                options={mapDestinationsToCombo(
                  !!supplier?.sapId
                    ? destinations?.filter(destination =>
                        destinationCodesFromPurchaseOrders.includes(destination.plantId),
                      )
                    : destinations,
                )}
                onChange={handleDestinationSelect}
              />
            </Form.Item>
          </StyledRow>
          {/* ORDEN DE COMPRA */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.purchase_order')}
              name="documentNumberId"
              rules={[
                {
                  required: enableDocument,
                  message: isRequiredMsg,
                },
              ]}
            >
              <Select
                showSearch
                filterOption={handleFilterOptions}
                disabled={!tripTypeValue && (!enableDocument || !plantId)}
                placeholder={!!supplier?.sapId && t('globals.placeholder_select')}
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                options={purchaseOrders?.map(
                  ({ DocumentNumberID, ProposedDeliveryDate, Inco1, Inco2 }) => ({
                    label: `${DocumentNumberID} - ${OtherUtils.capitalize(
                      dayjs(ProposedDeliveryDate).format('MMM-YY'),
                    )} - ${Inco1}/${Inco2}`,
                    value: DocumentNumberID,
                  }),
                )}
                onChange={handlePurchaseDocumentChange}
              />
            </Form.Item>
          </StyledRow>
          {/* TIPO VIAJE */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label="Incoterm"
              rules={[{ required, message: isRequiredMsg }]}
              name="tripType"
            >
              <TripTypeDisplay tripType={tripTypeValue} />
            </Form.Item>
          </StyledRow>
          {/* MATERIAL */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.material')}
              name="materialId"
              rules={[
                {
                  required,
                  message: isRequiredMsg,
                },
              ]}
            >
              <Select
                showSearch
                onChange={handleMaterialsOptions}
                filterOption={handleFilterOptions}
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder={t('globals.placeholder_select')}
                options={materialsOptionsSapCode}
              />
            </Form.Item>
          </StyledRow>
          {/* EQUIPO */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.equipment')}
              rules={[
                {
                  required,
                  message: isRequiredMsg,
                },
              ]}
              name="equipmentId"
            >
              <Select
                showSearch
                filterOption={handleFilterOptions}
                allowClear
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder={t('globals.placeholder_select')}
                options={equipments?.map(equipment => ({
                  label: equipment.nameShort,
                  value: equipment.id,
                }))}
              />
            </Form.Item>
          </StyledRow>
          {/* FECHA */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={
                <>
                  {required && <div className="required-asterisk">*</div>}
                  <span>{t('suppliers_router.new_ticket_modal.date')}</span>
                </>
              }
              rules={[
                {
                  required,
                  message: isRequiredMsg,
                },
              ]}
            >
              <FlexCol className="ticket-form__dates-column">
                <FlexRow>
                  <DatePicker
                    multiple={tripTypeValue !== TripTypeEnum.CFR}
                    onChange={date => {
                      if (!date) {
                        clearDates()
                        return
                      }
                      const thisDates = Array.isArray(date) ? date : [date]
                      handleChangeDate(thisDates as Dayjs[])
                    }}
                    open={isCalendarVisible}
                    renderExtraFooter={calendarFooter}
                    onClick={() => {
                      setIsCalendarVisible(true)
                    }}
                    value={dates?.map(date => dayjs(date)) ?? undefined}
                    maxTagCount="responsive"
                    required
                    allowClear
                    format={DATE_KEYS.FORMAT.DAY_SHORT}
                    className={classesToString([
                      'ticket-form__date-picker-input',
                      isWarning && 'ticket-form__is-warning',
                    ])}
                    minDate={isTripCfr ? today : tomorrow}
                    maxDate={
                      maxAllowedDaysForSupplierRequest
                        ? dayjs().add(maxAllowedDaysForSupplierRequest, 'day')
                        : undefined
                    }
                  />
                </FlexRow>
                <FlexRow>
                  {!!dates?.length
                    ? `${t('suppliers_router.new_ticket_modal.dates_quantity')} ${dates.length}`
                    : ''}
                </FlexRow>
              </FlexCol>
            </Form.Item>
          </StyledRow>
          {/* HORARIO DE CORTE */}
          {!isTripCfr && (
            <StyledRow className="new-ticket-form-row cutoff-time-info-row">
              <span className={classesToString([isWarning && 'ticket-form__is-warning'])}>
                {t('globals.cutoff_time_info', { time: cutoffTime })}
              </span>
            </StyledRow>
          )}
          {/* REMITO */}
          {isTripCfr && (
            <>
              <StyledRow className="carrier-form-row">
                <FlexRow style={{ alignItems: 'start', gap: '8px', flex: 1 }}>
                  <Form.Item
                    label={
                      <DeliveryNoteLabel showNotFoundWarning={showDeliveryNoteNotFoundWarning}>
                        {t('suppliers_router.new_ticket_modal.delivery_note')}
                      </DeliveryNoteLabel>
                    }
                    name="number"
                    rules={[
                      {
                        validator: (_, value) => {
                          const normalized = DeliveryNoteUtils.normalizeValue(value)
                          if (!normalized) {
                            return Promise.reject(new Error(isRequiredMsg))
                          }
                          if (
                            !DeliveryNoteUtils.isValidDeliveryNote(
                              normalized,
                              deliveryNoteMinLength,
                              deliveryNoteMaxLength,
                            )
                          ) {
                            return Promise.reject(new Error(deliveryNoteValidationMessage))
                          }
                          return Promise.resolve()
                        },
                      },
                    ]}
                  >
                    {!isTripCfr && editable ? (
                      <Select
                        allowClear
                        showSearch
                        getPopupContainer={triggerNode => triggerNode.parentNode}
                        placeholder={t('globals.placeholder_select')}
                        options={deliveryNotesOptions}
                        loading={isDeliveryNotesLoading || isSyncingDeliveryNotes}
                        notFoundContent={t('suppliers_router.new_ticket_modal.no_delivery_notes')}
                        onChange={handleDeliveryNoteChange}
                        disabled={!isDeliveryNoteFieldEnabled}
                      />
                    ) : (
                      <Select
                        allowClear
                        showSearch
                        getPopupContainer={triggerNode => triggerNode.parentNode}
                        placeholder={t('globals.placeholder_select')}
                        options={deliveryNotesOptions}
                        loading={isDeliveryNotesLoading || isSyncingDeliveryNotes}
                        notFoundContent={t('suppliers_router.new_ticket_modal.no_delivery_notes')}
                        onChange={value => {
                          handleDeliveryNoteChange(value)
                          const input = document.activeElement as HTMLElement
                          if (input) input.blur()
                        }}
                        disabled={!isDeliveryNoteFieldEnabled}
                        {...(currentDocumentNumberId ? {} : { mode: 'tags' as const, maxCount: 1 })}
                      />
                    )}
                  </Form.Item>
                  <Tooltip title={t('suppliers_router.new_ticket_modal.refresh_delivery_notes')}>
                    <Button
                      type="primary"
                      icon={<SyncOutlined spin={isSyncingDeliveryNotes} />}
                      onClick={syncDeliveryNotes}
                      disabled={!isDeliveryNoteFieldEnabled || isSyncingDeliveryNotes}
                      loading={isSyncingDeliveryNotes}
                      className="contract-search-button"
                    />
                  </Tooltip>
                </FlexRow>
              </StyledRow>
              {/* TICKET SAP */}
              <StyledRow className="carrier-form-row ticket-sap-row">
                <Form.Item
                  label={t('suppliers_router.new_ticket_modal.ticketSap')}
                  name="ticketSap"
                  hasFeedback
                  rules={[
                    {
                      required: false,
                      message: isRequiredMsg,
                    },
                    {
                      pattern: /^\d+$/,
                      message: t('message_ticket_sap_number'),
                    },
                  ]}
                >
                  <StyledInput
                    allowClear
                    onChange={({ target }) => {
                      const value = target.value
                      handlePrimaryButtonDisabling(value.length > 0 && !/^\d+$/.test(value))
                    }}
                  />
                </Form.Item>
              </StyledRow>
            </>
          )}
        </FlexCol>
        <FlexCol className="form-section">
          <StyledRow className="new-ticket-form-row comments">
            <Form.Item label={t('ticket_modal.comments')} name="comments">
              <TextArea maxLength={1200} rows={3} allowClear />
            </Form.Item>
          </StyledRow>
        </FlexCol>
      </FlexCol>
    </Form>
  )
}

export default AddTicketFormSupplier
